import type { ReactNode } from 'react';
import { useLiveClockDisplay, useLiveClockParts } from '@/hooks/useLiveClock';
import type { LiveClockState } from '@/domain/live-clock';
import { cn } from '@/lib/cn';

export interface LiveClockTextProps {
  liveClockState?: LiveClockState;
  clock?: string;
  translatePeriod?: (key: string) => string;
  className?: string;
  /**
   * `inline` — single line (`2HT 67'`).
   * `stacked` — period on the first row, time on the second.
   */
  layout?: 'inline' | 'stacked';
  /** Rendered before the period on the first stacked row (e.g. live icon). */
  leading?: ReactNode;
}

/** Renders a live clock, ticking seconds locally when extended match time is available. */
export function LiveClockText({
  liveClockState,
  clock,
  translatePeriod,
  className,
  layout = 'inline',
  leading,
}: LiveClockTextProps) {
  const tickingClock = useLiveClockDisplay(
    layout === 'inline' ? liveClockState : undefined,
    translatePeriod,
  );
  const parts = useLiveClockParts(
    layout === 'stacked' ? liveClockState : undefined,
    translatePeriod,
  );

  if (layout === 'stacked') {
    const period = parts?.period;
    const time = parts?.time;

    if (!period && !time && !clock) {
      return leading ? <span className={cn(className)}>{leading}</span> : null;
    }

    if (!period && !time && clock) {
      return (
        <span className={cn('flex flex-col items-center gap-0.5', className)}>
          {leading ? (
            <span className="flex items-center gap-1">
              {leading}
              <span className="whitespace-nowrap">{clock}</span>
            </span>
          ) : (
            <span className="whitespace-nowrap">{clock}</span>
          )}
        </span>
      );
    }

    return (
      <span className={cn('flex flex-col items-center gap-0.5', className)}>
        {(leading || period) && (
          <span className="flex items-center gap-1">
            {leading}
            {period ? <span className="whitespace-nowrap">{period}</span> : null}
          </span>
        )}
        {time ? <span className="whitespace-nowrap">{time}</span> : null}
      </span>
    );
  }

  const label = tickingClock ?? clock;
  if (!label) return null;
  return <span className={cn(className)}>{label}</span>;
}
